import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { PaginatedDataTable } from "../components/ui/PaginatedDataTable";
import { RfqDetails } from "./RFQDetails";
import { API_ROOT_PATH, fetchRfqDashboard, fetchStoresWhereStaffAssigned, currencyCodeToSymbolMap, fetchInvoiceHeaderForStore } from "../apiHelper";
import { useToast } from "../hooks/useToast";
import SearchableSelectBox from "../components/ui/SearchableSelectBox";
import Button from "../components/ui/Button";

export default function RfqDashboard() {
    const [data, setData] = useState({ columns: [], rows: [], total: 0 });
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ offset: 0, limit: 10 });
    const [selectedRfq, setSelectedRfq] = useState(null);
    const [selectedStoreId, setSelectedStoreId] = useState("");
    const [storeOptions, setStoreOptions] = useState([]);
    const [loadingStores, setLoadingStores] = useState(false);
    const [headerImageUrl, setHeaderImageUrl] = useState("");

    const { showToast } = useToast();
    const [user, setUser] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const stored = localStorage.getItem('user');
        if (stored) {
            setUser(JSON.parse(stored));
        } else {
            showToast('Please log in to create RFQs.', 'info');
            navigate('/login');
        }
    }, [showToast, navigate]);

    const loadStores = useCallback(async () => {
        if (!user) return;
        setLoadingStores(true);
        try {
            const stores = await fetchStoresWhereStaffAssigned(user);
            if (stores && stores.length > 0) {
                const mappedStores = stores.filter(s => s.storetype !=='WHS').map(store => ({
                    label: store.identifier || `Branch ${store.store_id}`,
                    value: store.store_id
                }));
                setStoreOptions(mappedStores);
                setSelectedStoreId(mappedStores[0].value);
            }
        } catch (err) {
            showToast(err.response?.data?.detail || 'Failed to load branches', 'error');
        } finally {
            setLoadingStores(false);
        }
    }, [user, showToast]);

    useEffect(() => {
        loadStores();
    }, [loadStores]);

    const fetchRfqs = useCallback(async () => {
        if (!selectedStoreId) return;
        setLoading(true);
        try {
            const response = await fetchRfqDashboard(
                {
                    store_id: selectedStoreId,
                    offset: pagination.offset,
                    limit: pagination.limit
                }
            );
            setData(response)
        } catch (err) {
            console.error("Failed to fetch RFQs", err);
        } finally {
            setLoading(false);
        }
    }, [selectedStoreId, pagination]);

    useEffect(() => {
        const getInvoiceHeaders = async () => {
            if (!selectedStoreId) return;
            try {
                const headers = await fetchInvoiceHeaderForStore(selectedStoreId);
                if (headers && headers.length > 0 && headers[0].attachmenturl) {
                    const root = API_ROOT_PATH.endsWith('/') ? API_ROOT_PATH.slice(0, -1) : API_ROOT_PATH;
                    setHeaderImageUrl(`${root}${headers[0].attachmenturl}`);
                } else {
                    setHeaderImageUrl("");
                }
            } catch (err) {
                console.error("Failed to fetch invoice headers:", err);
            }
        };

        getInvoiceHeaders();
    }, [selectedStoreId]);

    useEffect(() => {
        fetchRfqs();
    }, [fetchRfqs]);

    if (selectedRfq) {
        return <RfqDetails rfq={selectedRfq} headerImageUrl={headerImageUrl} onBack={() => setSelectedRfq(null)} />;
    }

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
            {/* Header section */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">RFQ Management</h1>
                    <p className="text-sm text-slate-500">Manage and view incoming requests for quotation.</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="w-64">
                        <SearchableSelectBox
                            name="storeId"
                            value={selectedStoreId}
                            onChange={(e) => setSelectedStoreId(e.target.value)}
                            options={storeOptions}
                            loading={loadingStores}
                            placeholder="Select Store Branch"
                        />
                    </div>
                    <Button
                        variant="primary"
                        onClick={() => navigate('new')}
                        className="flex items-center gap-1.5 whitespace-nowrap mb-4"
                    >
                        <Plus size={16} /> Create RFQ
                    </Button>
                </div>
            </div>

            {/* Main Table */}
            <PaginatedDataTable
                columns={data.columns}
                rows={data.rows}
                total={data.total}
                offset={pagination.offset}
                limit={pagination.limit}
                isLoading={loading}
                onPageChange={(newOffset) => setPagination((prev) => ({ ...prev, offset: newOffset }))}
                renderCell={(row, key) => {
                    if (key === "access_type") {
                        return (
                            <span className={`px-2 py-1 rounded-md text-xs font-semibold ${row.accesstype_code === 1 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"
                                }`}>
                                {row.access_type}
                            </span>
                        );
                    }
                    if (key === "total_amount") {
                        const currencySymbol = currencyCodeToSymbolMap[row.items?.[0]?.currency] || "$";
                        return (
                            <span className="font-semibold text-slate-900">
                                {currencySymbol}{row.total_amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                        );
                    }
                    if (key === "created_on") {
                        return row.created_on ? new Date(row.created_on).toLocaleDateString() : "-";
                    }
                    if (key === "actions") {
                        return (
                            <button
                                onClick={() => setSelectedRfq(row)}
                                className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-md transition"
                            >
                                View Details
                            </button>
                        );
                    }
                    return row[key];
                }}
            />
        </div>
    );
}